import { useCallback } from 'react';
import { RawModel } from '@/types/rawModel';
import { PendingOutput } from '@/app/assets/actions/rawModelPendingOutputs';
import { useQueryClient } from '@tanstack/react-query';
import { useAssetsParams, useAssetsQueryKey } from '../../../hooks/useAssetsQuery';
import { Page } from '@/types/pagination';

export type OptimisticAssetsAction =
  | {
      action: 'add';
      payload: RawModel[];
    }
  | {
      action: 'update';
      payload: Partial<RawModel>[];
    }
  | {
      action: 'remove';
      payload: number[];
    }
  | {
      action: 'addFinishedOutput';
      payload: PendingOutput[];
    };

const reducer = (prev: RawModel[], action: OptimisticAssetsAction): RawModel[] => {
  switch (action.action) {
    case 'add':
      return [...prev, ...action.payload.filter((a) => !prev.some((i) => i.id === a.id))];
    case 'remove':
      return [
        ...prev
          .filter((p) => !action.payload.some((a) => a === p.id))
          .map((p) => ({
            ...p,
            rapidModels: p.rapidModels.filter((r) => !action.payload.some((p) => p === r.id)),
          })),
      ];
    case 'update':
      return [
        ...prev.map((p) => ({
          ...p,
          ...action.payload.find((i) => i.id === p.id),
        })),
      ];
    case 'addFinishedOutput':
      return prev.map((rawAsset) => {
        if (!action.payload.some((a) => a.rawModelId === rawAsset.id)) {
          return rawAsset;
        }
        return {
          ...rawAsset,
          rapidModels: [
            ...rawAsset.rapidModels,
            ...action.payload
              .filter((f) => f.rawModelId === rawAsset.id)
              .map((f) => ({
                name: f.name,
                id: f.outputId,
                assetType: 'output',
                preset_name: f.workflowName,
                created_at: new Date().toISOString(),
                disabled: true,
              })),
          ],
        } as RawModel;
      });
  }
};

export const useOptimisticAssets = () => {
  const queryClient = useQueryClient();
  const { page, query, onlyOutputs } = useAssetsParams();
  const AssetsKeys = useAssetsQueryKey(query, page, onlyOutputs);

  const dispatchAssetActionWithCache = useCallback(
    (action: OptimisticAssetsAction) => {
      const assets = queryClient.getQueryData<Page<RawModel>>(AssetsKeys);
      if (!assets) {
        return;
      }

      const updatedAssets = reducer(assets.data, action);

      queryClient.setQueryData<Page<RawModel>>(AssetsKeys, () => ({
        ...assets,
        data: updatedAssets,
      }));
    },
    [AssetsKeys, queryClient]
  );

  return {
    dispatchAssetAction: dispatchAssetActionWithCache,
  };
};
